import { isNil, omitBy } from "lodash";

import * as pageFilterSelectors from "@/redux/sales-order/page-filter/page-filter.selectors";
import * as searchFilterSelectors from "@/redux/sales-order/search-filter/search-filter.selectors";
import * as sortSelectors from "@/redux/sales-order/sort/sort.selectors";

import { MODULE } from "./sales-order.actionTypes";

export const getQueryKey = (state) => {
  return [MODULE, { ...getQueryKeyFilter(state) }];
};

export const getQueryKeyFilter = (state) => {
  const search = searchFilterSelectors.getSearch(state);
  const columns = getCompanySchemas(state);
  const pagination = getPagination(state);
  const pageFilter = pageFilterSelectors.getFilter(state);
  const sort = sortSelectors.getSort(state);
  const aggregations = getAggregationsParam(state);
  const columnAggregations = getColumnAggregations(state);

  const filters = {
    select_only: columns.filter((c) => !c.isHidden).map((c) => c.key),
    q: search || undefined,
    default_filters: pageFilter
      ? omitBy(
          Object.fromEntries(
            pageFilter
              .filter(({ id }) => !columns.find((c) => c.key === id)?.isCustom)
              .map(({ id, values }) => [id, values])
          ),
          isNil
        )
      : {},
    custom_filters: pageFilter
      ? omitBy(
          Object.fromEntries(
            pageFilter
              .filter(({ id }) => columns.find((c) => c.key === id)?.isCustom)
              .map(({ id, values }) => [id, values])
          ),
          isNil
        )
      : {},
    sorting: sort
      .filter((s) => s.id)
      .map((s) => {
        const column = columns.find((c) => c.key === s.id);

        return {
          field_name: s.id,
          sort_by: s.desc ? "desc" : "asc",
          ...(column?.isCustom ? { is_custom: true, schema_type: column.schemaType } : {}),
        };
      }),
    aggregations,
    columnAggregations,
    ...pagination,
  };

  return filters;
};

export const isFetchingCompanySchemasStatus = (state) =>
  state.getIn([MODULE, "isFetchingCompanySchemasStatus"]);

export const isFetchingCustomSchemasStatus = (state) =>
  state.getIn([MODULE, "isFetchingCustomSchemasStatus"]);

export const isSavingCompanySchemasStatus = (state) =>
  state.getIn([MODULE, "isSavingCompanySchemasStatus"]);

export const getCompanySchemas = (state) => state.getIn([MODULE, "companySchemas"]);

export const getDefaultCompanySchemas = (state) => state.getIn([MODULE, "defaultCompanySchemas"]);

export const getCustomSchemas = (state) => state.getIn([MODULE, "customSchemas"]);

export const getSelectedRows = (state) => state.getIn([MODULE, "selectedRows"]);

export const getFormattedSelectedRows = (state) => {
  const selectedRows = state.getIn([MODULE, "selectedRows"]);
  return Object.fromEntries(selectedRows.map(({ uid }) => [uid, true]));
};

export const getPagination = (state) => state.getIn([MODULE, "pagination"]);

export const isDuplicateOrderAlertVisible = (state) =>
  state.getIn([MODULE, "isDuplicateOrderAlertVisible"]);

export const isDuplicatingOrderStatus = (state) =>
  state.getIn([MODULE, "isDuplicatingOrderStatus"]);

export const isExportingModalVisible = (state) => state.getIn([MODULE, "isExportingModalVisible"]);

export const isDeleteCustomColumnModalVisible = (state) =>
  state.getIn([MODULE, "isDeleteCustomColumnModalVisible"]);

export const getActiveOrder = (state) => state.getIn([MODULE, "activeOrder"]);

export const getActiveColumn = (state) => state.getIn([MODULE, "activeColumn"]);

export const isDeletingColumnStatus = (state) => state.getIn([MODULE, "isDeletingColumnStatus"]);

export const getColumnAggregations = (state) => {
  const columnsRaw = getCompanySchemas(state);
  const columns = columnsRaw?.toJS?.() ?? columnsRaw ?? [];
  const list = Array.isArray(columns) ? columns : [];
  return Object.fromEntries(
    list
      .filter((c) => c?.key != null)
      .map((c) => [
        c.key,
        { showTotal: c.showTotal ?? false, showAverage: c.showAverage ?? false },
      ])
  );
};

export const getAggregationsParam = (state) => {
  const columnsRaw = getCompanySchemas(state);
  const columns = columnsRaw?.toJS?.() ?? columnsRaw ?? [];
  const list = Array.isArray(columns) ? columns : [];

  const fields = [];
  const customFields = [];

  for (const c of list) {
    if ((c?.showTotal || c?.showAverage) && c?.key != null) {
      if (c.isCustom) {
        customFields.push(c.key);
      } else {
        fields.push(c.key);
      }
    }
  }

  if (fields.length === 0 && customFields.length === 0) {
    return;
  }

  return { fields, custom_fields: customFields };
};
